import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { API_URL } from '../tokens/api-url.token';
import { AuthStore } from '../stores/auth.store';

export interface LoginResponse {
  token: string;
  email: string;
  fullName: string;
  phoneNumber: string;
  address: string;
  isBusinessUser: boolean;
  isAdmin: boolean;
}

export interface CustomerSummary {
  id: string;
  fullName: string;
  companyName?: string;
  email: string;
}

export interface SwitchCustomerResponse {
  token: string;
  actingAsUserId: string;
  actingAsUserName: string;
  actingAsCompanyName?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);
  private readonly authStore = inject(AuthStore);

  login(email: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}auth/login`, { email, password }).pipe(
      tap(res => {
        this.authStore.setToken(res.token);
        this.authStore.setUser({
          email: res.email,
          fullName: res.fullName,
          phoneNumber: res.phoneNumber,
          address: res.address,
          isBusinessUser: res.isBusinessUser,
          isAdmin: res.isAdmin ?? false,
        });
      })
    );
  }

  fillUserData(): Observable<LoginResponse> {
    return this.http.get<LoginResponse>(`${this.apiUrl}auth/profile`).pipe(
      tap(res => {
        this.authStore.setUser({
          email: res.email,
          fullName: res.fullName,
          phoneNumber: res.phoneNumber,
          address: res.address,
          isBusinessUser: res.isBusinessUser,
          isAdmin: res.isAdmin ?? false,
        });
      })
    );
  }

  getCustomers(): Observable<CustomerSummary[]> {
    return this.http.get<CustomerSummary[]>(`${this.apiUrl}admin/customers`);
  }

  switchCustomer(customerId: string): Observable<SwitchCustomerResponse> {
    return this.http.post<SwitchCustomerResponse>(
      `${this.apiUrl}admin/switch-customer/${customerId}`,
      {}
    );
  }

  forgotPassword(email: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}auth/forgot-password`, { email });
  }

  confirmEmail(userId: string, token: string): Observable<void> {
    const params = new HttpParams().set('userId', userId).set('token', token);
    return this.http.get<void>(`${this.apiUrl}auth/confirm-email`, { params });
  }

  resetPasswordValidate(userId: string, token: string): Observable<void> {
    const params = new HttpParams().set('userId', userId).set('token', token);
    return this.http.get<void>(`${this.apiUrl}auth/reset-password/validate`, { params });
  }

  resetPassword(userId: string, token: string, newPassword: string, newPasswordConfirmation: string): Observable<void> {
    const params = new HttpParams().set('userId', userId).set('token', token);
    return this.http.post<void>(`${this.apiUrl}auth/reset-password`, { newPassword, newPasswordConfirmation }, { params });
  }
}
